import { Text, Box, Divider, Stack } from '@ltvco/refresh-lib/theme';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  FileDownloadOutlined,
  ShoppingCart,
} from '@mui/icons-material';
import { useState } from 'react';

import { DocketEntry } from 'routes/Dashboard/CourtCases/types';
import { formatCourtDateWithTime } from 'routes/Dashboard/CourtCases/utils';

import {
  GrayBorderStack,
  NumberText,
  DarkenedBox,
  ActionButton,
} from './components';

interface CourtCaseDocketBoxProps {
  docketEntry: DocketEntry;
  indexString: string;
  handleBuyOnPacer: (pacerDocId: string, entryNumber?: number | null) => void;
}

export const CourtCaseDocketBox = ({
  docketEntry,
  indexString,
  handleBuyOnPacer,
}: CourtCaseDocketBoxProps) => {
  const [collapse, setCollapse] = useState(false);

  const shouldShowDownloadButton =
    docketEntry?.recap_documents[0]?.is_available &&
    docketEntry?.recap_documents[0]?.filepath_ia;
  const shouldShowMainBuyButton =
    docketEntry?.entry_number && docketEntry?.recap_documents[0]?.pacer_doc_id;

  return (
    <GrayBorderStack spacing={2}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <NumberText>{indexString}</NumberText>
          <Text fontWeight="bold">
            Date Filed: {formatCourtDateWithTime(docketEntry.date_filed)}
          </Text>
        </Box>
        {docketEntry?.recap_documents?.length > 1 ? (
          collapse ? (
            <KeyboardArrowDown onClick={() => setCollapse(false)} />
          ) : (
            <KeyboardArrowUp onClick={() => setCollapse(true)} />
          )
        ) : null}
      </Box>
      {docketEntry?.recap_documents?.length > 0 ? (
        <DarkenedBox>
          <Text variant="body2" color="text.secondary">
            Main Document{' '}
            {docketEntry?.recap_documents[0]?.description
              ? `- ${docketEntry?.recap_documents[0]?.description}`
              : ''}
          </Text>
          {shouldShowDownloadButton || shouldShowMainBuyButton ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mt: 1,
              }}
            >
              <ActionButton
                color="primary"
                startIcon={<FileDownloadOutlined />}
                size="small"
                onClick={() =>
                  window.open(
                    docketEntry?.recap_documents[0]?.filepath_ia,
                    '_blank'
                  )
                }
                sx={!shouldShowDownloadButton ? { opacity: 0 } : {}}
                disabled={!shouldShowDownloadButton}
              >
                Download PDF
              </ActionButton>
              {shouldShowMainBuyButton && shouldShowDownloadButton ? (
                <Divider orientation="vertical" />
              ) : null}
              <ActionButton
                color="primary"
                startIcon={<ShoppingCart />}
                size="small"
                onClick={() =>
                  handleBuyOnPacer(
                    docketEntry?.recap_documents[0]?.pacer_doc_id || '',
                    docketEntry?.entry_number || null
                  )
                }
                sx={!shouldShowMainBuyButton ? { opacity: 0 } : {}}
                disabled={!shouldShowMainBuyButton}
              >
                Buy on PACER
              </ActionButton>
            </Box>
          ) : null}
        </DarkenedBox>
      ) : null}
      {!collapse && docketEntry?.recap_documents?.length > 1 ? (
        <Stack divider={<Divider />} spacing={1}>
          {docketEntry?.recap_documents?.slice(1).map((document) => (
            <Box
              key={document.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Text variant="body2" color="text.secondary">
                {document.description}
              </Text>
              <ShoppingCart
                onClick={() =>
                  handleBuyOnPacer(
                    document?.pacer_doc_id,
                    docketEntry?.entry_number || null
                  )
                }
                sx={{
                  fontSize: 16,
                  color: 'text.secondary',
                  cursor: 'pointer',
                }}
              />
            </Box>
          ))}
        </Stack>
      ) : null}
    </GrayBorderStack>
  );
};
